import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  tag: string;
  title: string;
  subtitle: string;
  image: string;
  to: string;
  meta: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const go = (index: number) => setActive((index + slides.length) % slides.length);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 焦点图`}
              title={`${slide.title} 河北彩花影视焦点`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          </div>
        ))}

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 md:px-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-accent-950">
                <i className="ri-fire-line"></i>
                {slides[active].tag}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {slides[active].title}
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                {slides[active].subtitle}
              </p>
              <p className="mt-3 text-xs text-primary-200">{slides[active].meta}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={slides[active].to}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-400"
                >
                  查看详情
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  浏览全部作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-3">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(active - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <div className="flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到第${index + 1}张`}
                onClick={() => go(index)}
                className={`h-2 rounded-full transition-all ${
                  index === active ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50/80'
                }`}
              ></button>
            ))}
          </div>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(active + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}